import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  Github,
  Globe2,
  Loader2,
  LockKeyhole,
  Mail,
  Moon,
  ShieldCheck,
  Sparkles,
  Sun,
  UserPlus,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { buildFirstPartyAuthorizeUrl } from '../lib/authHandoff';
import { idAuth, type IdAuthProvider, type IdMe } from '../lib/idAuth';
import {
  subscribeToIdVerificationStatus,
  type IdVerificationDeliveryStatus,
} from '../lib/idVerificationRealtime';

type Mode = 'login' | 'signup' | 'forgot' | 'verify' | 'reset' | 'mfa' | 'error';
type VerificationPurpose = 'signup' | 'recovery';

const pathMode = (pathname: string): Mode => {
  if (pathname.startsWith('/signup')) return 'signup';
  if (pathname.startsWith('/forgot-password')) return 'forgot';
  if (pathname.startsWith('/verify-email')) return 'verify';
  if (pathname.startsWith('/reset-password')) return 'reset';
  if (pathname.startsWith('/mfa')) return 'mfa';
  if (pathname.startsWith('/error')) return 'error';
  return 'login';
};

const messageFrom = (error: unknown) =>
  error instanceof Error ? error.message : 'Something went wrong. Try again.';

const fieldClass =
  'w-full rounded-[18px] border border-black/10 bg-black/[0.035] px-4 py-3.5 text-sm text-[#17171a] outline-none transition placeholder:text-black/30 focus:border-[#6f55ef]/55 focus:bg-white focus:ring-4 focus:ring-[#6f55ef]/10 dark:border-white/10 dark:bg-white/[0.055] dark:text-white dark:placeholder:text-white/28 dark:focus:border-[#9b88ff]/60 dark:focus:bg-white/[0.075] dark:focus:ring-[#8a6cff]/15';

const BrandMark = ({ compact = false }: { compact?: boolean }) => (
  <span
    aria-hidden="true"
    className={`grid shrink-0 place-items-center rounded-[15px] bg-[#17171a] font-black text-white shadow-sm dark:bg-white dark:text-[#111114] ${compact ? 'h-9 w-9 text-xs' : 'h-11 w-11 text-sm'}`}
  >
    W
  </span>
);

const Shell = ({
  children,
  title,
  subtitle,
  eyebrow,
}: {
  children: ReactNode;
  title: string;
  subtitle: ReactNode;
  eyebrow?: string;
}) => {
  const { isDark, toggleTheme } = useTheme();
  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-[#f1f1f7] px-3 py-3 text-[#17171a] transition-colors duration-300 dark:bg-[#08080a] dark:text-white sm:px-6 sm:py-6 lg:p-0">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_12%_4%,rgba(113,91,236,0.13),transparent_30%),radial-gradient(circle_at_88%_88%,rgba(92,207,255,0.12),transparent_30%)] dark:bg-[radial-gradient(circle_at_12%_4%,rgba(113,91,236,0.18),transparent_30%),radial-gradient(circle_at_88%_88%,rgba(92,207,255,0.08),transparent_30%)]" />
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? 'Use light appearance' : 'Use dark appearance'}
        className="fixed right-4 top-4 z-20 grid h-10 w-10 place-items-center rounded-full border border-black/8 bg-white/80 text-[#333338] shadow-sm backdrop-blur-xl transition hover:scale-105 dark:border-white/10 dark:bg-[#17171b]/80 dark:text-white sm:right-6 sm:top-6"
      >
        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <main className="relative mx-auto grid min-h-[calc(100dvh-24px)] w-full max-w-[1120px] overflow-hidden rounded-[30px] border border-black/[0.06] bg-white shadow-[0_28px_90px_rgba(59,46,117,0.14)] dark:border-white/10 dark:bg-[#111114] dark:shadow-[0_30px_110px_rgba(0,0,0,0.46)] sm:min-h-[680px] lg:min-h-[100dvh] lg:max-w-none lg:grid-cols-[0.92fr_1.08fr] lg:rounded-none lg:border-0 lg:shadow-none dark:lg:shadow-none">
        <aside className="relative hidden overflow-hidden bg-[#7458ef] p-9 text-white lg:flex lg:min-h-[100dvh] lg:flex-col lg:p-12 xl:p-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_12%,rgba(134,230,255,0.95),transparent_28%),radial-gradient(circle_at_82%_15%,rgba(255,172,240,0.9),transparent_27%),radial-gradient(circle_at_48%_56%,rgba(40,16,174,0.95),transparent_43%),linear-gradient(145deg,#41c8fa_0%,#8064f7_44%,#d985f2_100%)]" />
          <div className="absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(255,255,255,.15)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.15)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:linear-gradient(to_bottom,black,transparent_78%)]" />
          <div className="relative flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-[15px] bg-white text-sm font-black text-[#17171a] shadow-lg">W</span>
            <div>
              <p className="text-sm font-bold">Wersee ID</p>
              <p className="text-[11px] text-white/70">One identity. Every Wersee service.</p>
            </div>
          </div>

          <div className="relative my-auto py-12">
            <div className="mx-auto w-[82%] rounded-[30px] border border-white/25 bg-black/20 p-5 shadow-[0_25px_70px_rgba(38,16,125,0.35)] backdrop-blur-2xl">
              <div className="flex items-center justify-between">
                <span className="rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold text-white/80">Secure by design</span>
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="mt-14 space-y-3">
                {['Your Wersee profile', 'Every connected app', 'Realtime security status'].map((label, index) => (
                  <div key={label} className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-3 py-3 backdrop-blur-xl">
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-white text-[10px] font-black text-[#5538d4]">{index + 1}</span>
                    <span className="text-xs font-semibold">{label}</span>
                    <Check className="ml-auto h-3.5 w-3.5 text-emerald-200" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <Sparkles className="mb-5 h-6 w-6" />
            <p className="max-w-[390px] text-[30px] font-black leading-[1.05] tracking-[-0.035em]">Your work, people and payments. One beautifully secure identity.</p>
            <div className="mt-6 flex items-center gap-2 text-[11px] font-medium text-white/72">
              <Globe2 className="h-4 w-4" /> Protected across every Wersee service
            </div>
          </div>
        </aside>

        <section className="flex min-h-0 flex-col px-5 pb-8 pt-5 sm:px-10 sm:pb-10 sm:pt-8 lg:px-16 lg:py-12">
          <Link to="/" className="mb-8 flex w-fit items-center gap-3 lg:hidden">
            <BrandMark compact />
            <span>
              <strong className="block text-xs">Wersee ID</strong>
              <span className="block text-[9px] text-black/40 dark:text-white/40">One identity. Every Wersee service.</span>
            </span>
          </Link>

          <div className="my-auto w-full max-w-[470px] self-center">
            <header className="mb-7">
              {eyebrow && <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[#6b50e8] dark:text-[#a997ff]">{eyebrow}</p>}
              <h1 className="text-[30px] font-black leading-[1.08] tracking-[-0.035em] sm:text-[36px]">{title}</h1>
              <p className="mt-3 max-w-[420px] text-sm leading-6 text-black/48 dark:text-white/46">{subtitle}</p>
            </header>
            {children}
          </div>

          <footer className="mt-9 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] text-black/35 dark:text-white/30">
            <span>© Wersee</span>
            <a href="https://www.wersee.com/privacy" className="hover:text-black dark:hover:text-white">Privacy</a>
            <a href="https://www.wersee.com/help" className="hover:text-black dark:hover:text-white">Help</a>
          </footer>
        </section>
      </main>
    </div>
  );
};

const Notice = ({ error, children }: { error?: boolean; children: ReactNode }) => (
  <div
    role={error ? 'alert' : 'status'}
    className={`rounded-[18px] border px-4 py-3 text-sm leading-5 ${error ? 'border-red-500/15 bg-red-500/[0.07] text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200' : 'border-emerald-500/15 bg-emerald-500/[0.07] text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-200'}`}
  >
    {children}
  </div>
);

const SubmitButton = ({ busy, disabled, children }: { busy: boolean; disabled?: boolean; children: ReactNode }) => (
  <button
    type="submit"
    disabled={busy || disabled}
    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#17171a] px-5 py-3.5 text-sm font-bold text-white shadow-[0_10px_28px_rgba(23,23,26,0.18)] transition hover:-translate-y-0.5 hover:bg-black disabled:cursor-wait disabled:translate-y-0 disabled:opacity-45 dark:bg-white dark:text-[#111114] dark:hover:bg-white/90"
  >
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
    {children}
  </button>
);

const PasswordField = ({
  label,
  value,
  onChange,
  autoComplete,
  forgotHref,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  forgotHref?: string;
}) => {
  const [visible, setVisible] = useState(false);
  return (
    <label className="block space-y-2 text-xs font-semibold text-black/62 dark:text-white/58">
      <span className="flex items-center justify-between">
        {label}
        {forgotHref && <Link to={forgotHref} className="text-[#6b50e8] hover:text-[#5237d5] dark:text-[#ae9dff]">Forgot password?</Link>}
      </span>
      <div className="relative">
        <LockKeyhole className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/28 dark:text-white/28" />
        <input
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          minLength={8}
          required
          className={`${fieldClass} px-11`}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-black/35 hover:text-black dark:text-white/35 dark:hover:text-white"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </label>
  );
};

export const WerseeIdAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = pathMode(location.pathname);
  const redirect = params.get('redirect') || params.get('returnUrl') || params.get('authReturn') || 'https://www.wersee.com/workspace';
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(params.has('oauth_error') ? 'The provider sign-in was cancelled or could not be completed. Try again.' : null);
  const [notice, setNotice] = useState<string | null>(params.get('notice') === 'check-email' ? 'Check your inbox to confirm your Wersee account.' : null);
  const [providers, setProviders] = useState<IdAuthProvider[]>([]);
  const [rememberedAccount, setRememberedAccount] = useState<IdMe | null>(null);
  const [checkingRemembered, setCheckingRemembered] = useState(mode === 'login');
  const [showSignInOptions, setShowSignInOptions] = useState(true);
  const [mfa, setMfa] = useState<{ factorId: string; challengeId: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState<IdVerificationDeliveryStatus>(
    params.get('delivery') === 'sent' ? 'sent' : 'requested',
  );
  const [resendSeconds, setResendSeconds] = useState(30);
  const codeInput = useRef<HTMLInputElement>(null);
  const oauthCompletionStarted = useRef(false);

  const verificationPurpose: VerificationPurpose = params.get('purpose') === 'recovery' ? 'recovery' : 'signup';
  const verificationStatusTopic = params.get('verification') || '';
  const verificationDeliveryHint = params.get('delivery') === 'sent' ? 'sent' : 'requested';
  const googleProvider = useMemo(() => providers.find((item) => item.id === 'google' && item.enabled), [providers]);
  const handoffAuthorizeUrl = useMemo(() => params.get('handoff') === '1' ? buildFirstPartyAuthorizeUrl(params) : null, [params]);

  const navigateOrLeave = (destination: string, replace = false) => {
    const target = new URL(destination, window.location.origin);
    if (target.origin === window.location.origin) {
      navigate(`${target.pathname}${target.search}${target.hash}`, { replace });
      return;
    }
    if (replace) window.location.replace(target.toString());
    else window.location.assign(target.toString());
  };
  const finishSignIn = (fallback: string) => navigateOrLeave(handoffAuthorizeUrl || fallback, true);
  const shouldShowSignInOptions = mode !== 'login' || showSignInOptions || (!checkingRemembered && !rememberedAccount);
  const rememberedName = rememberedAccount?.identity.display_name || rememberedAccount?.identity.username || rememberedAccount?.user.email || 'your Wersee account';
  const authModeHref = (path: string) => `${path}${location.search || `?redirect=${encodeURIComponent(redirect)}`}`;

  useEffect(() => {
    const titles: Record<Mode, string> = {
      login: 'Sign in to Wersee ID', signup: 'Create your Wersee ID', forgot: 'Recover your Wersee ID', verify: 'Verify your Wersee ID', reset: 'Reset your Wersee password', mfa: 'Verify your Wersee ID', error: 'Wersee ID link expired',
    };
    document.title = titles[mode];
  }, [mode]);

  useEffect(() => {
    if (!['login', 'signup'].includes(mode)) return;
    void idAuth.providers().then((data) => setProviders(data.providers)).catch(() => setProviders([]));
  }, [mode]);

  useEffect(() => {
    if (mode !== 'verify') return;
    setDeliveryStatus(verificationDeliveryHint);
    return subscribeToIdVerificationStatus(verificationStatusTopic, (payload) => {
      if (!payload.status) return;
      setDeliveryStatus(payload.status);
      if (payload.status === 'sent') setNotice('Your verification code has been sent through Wersee ID.');
      if (payload.status === 'delivered') setNotice('Your verification email was delivered. Enter the code below.');
      if (['failed', 'bounced', 'suppressed'].includes(payload.status)) setError('The verification email could not be delivered. Check the address or request a new code.');
    });
  }, [mode, verificationDeliveryHint, verificationStatusTopic]);

  useEffect(() => {
    if (mode !== 'verify' || resendSeconds <= 0) return;
    const timer = window.setInterval(() => setResendSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [mode, resendSeconds]);

  useEffect(() => {
    if (mode !== 'login') {
      setRememberedAccount(null);
      setCheckingRemembered(false);
      setShowSignInOptions(true);
      return;
    }
    let active = true;
    setCheckingRemembered(true);
    void idAuth.session().then(({ account }) => {
      if (!active) return;
      if (!account) {
        setRememberedAccount(null);
        if (params.get('oauth_complete') === '1') setError('The provider sign-in did not create a Wersee session. Try again.');
        return;
      }
      if (params.get('oauth_complete') === '1' && !oauthCompletionStarted.current) {
        oauthCompletionStarted.current = true;
        setBusy(true);
        if (handoffAuthorizeUrl) {
          window.location.replace(handoffAuthorizeUrl);
          return;
        }
        void idAuth.continue(redirect).then((data) => window.location.replace(data.redirect)).catch((nextError) => {
          if (!active) return;
          oauthCompletionStarted.current = false;
          setError(messageFrom(nextError));
          setBusy(false);
        });
        return;
      }
      setRememberedAccount(account);
      setShowSignInOptions(false);
    }).catch(() => {
      if (active) setRememberedAccount(null);
    }).finally(() => {
      if (active) setCheckingRemembered(false);
    });
    return () => { active = false; };
  }, [handoffAuthorizeUrl, mode, params, redirect]);

  useEffect(() => {
    if (mode !== 'mfa') return;
    setBusy(true);
    setError(null);
    void idAuth.mfa().then(async (data) => {
      const factor = [...data.factors.totp, ...data.factors.phone].find((item) => item.status === 'verified') || data.factors.totp[0] || data.factors.phone[0];
      if (!factor) {
        navigate('/security?notice=mfa-not-enrolled', { replace: true });
        return;
      }
      const challenge = await idAuth.mfaChallenge(factor.id);
      setMfa({ factorId: factor.id, challengeId: challenge.challenge_id });
    }).catch((nextError) => setError(messageFrom(nextError))).finally(() => setBusy(false));
  }, [mode, navigate]);

  const submitCredentials = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'signup') {
        if (password !== confirm) throw new Error('The passwords do not match.');
        const data = await idAuth.signup({ email, password, display_name: displayName, redirect });
        if (data.confirmation_required) navigateOrLeave(data.redirect, true);
        else finishSignIn(data.redirect);
      } else {
        const data = await idAuth.login({ email, password, redirect });
        finishSignIn(data.redirect);
      }
    } catch (nextError) {
      setError(messageFrom(nextError));
    } finally {
      setBusy(false);
    }
  };

  const submitRecovery = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await idAuth.recover(email, redirect);
      navigateOrLeave(data.redirect, true);
    } catch (nextError) {
      setError(messageFrom(nextError));
    } finally {
      setBusy(false);
    }
  };

  const submitVerification = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await idAuth.verifyEmail({
        email,
        code: verificationCode,
        purpose: verificationPurpose,
        status_topic: verificationStatusTopic,
        redirect,
      });
      navigateOrLeave(data.redirect, true);
    } catch (nextError) {
      setError(messageFrom(nextError));
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async () => {
    if (resendSeconds > 0 || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const data = await idAuth.resendEmailVerification({ email, purpose: verificationPurpose, redirect });
      setVerificationCode('');
      setResendSeconds(30);
      navigateOrLeave(data.redirect, true);
    } catch (nextError) {
      setError(messageFrom(nextError));
      setBusy(false);
    }
  };

  const submitReset = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (password !== confirm) throw new Error('The passwords do not match.');
      const data = await idAuth.resetPassword(password);
      navigateOrLeave(data.redirect, true);
    } catch (nextError) {
      setError(messageFrom(nextError));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (event: FormEvent) => {
    event.preventDefault();
    if (!mfa) return;
    setBusy(true);
    setError(null);
    try {
      const data = await idAuth.mfaVerify(mfa.factorId, mfa.challengeId, mfaCode, redirect);
      finishSignIn(data.redirect);
    } catch (nextError) {
      setError(messageFrom(nextError));
    } finally {
      setBusy(false);
    }
  };

  const startOAuth = async (provider: 'google' | 'azure' | 'github') => {
    setBusy(true);
    setError(null);
    const callbackRedirect = new URL(window.location.href);
    callbackRedirect.pathname = '/login';
    callbackRedirect.searchParams.delete('oauth_error');
    callbackRedirect.searchParams.set('oauth_complete', '1');
    try {
      const data = await idAuth.oauthStart(provider, callbackRedirect.toString());
      navigateOrLeave(data.url);
    } catch (nextError) {
      setError(messageFrom(nextError));
      setBusy(false);
    }
  };

  const continueRememberedAccount = async () => {
    setBusy(true);
    setError(null);
    if (handoffAuthorizeUrl) {
      window.location.replace(handoffAuthorizeUrl);
      return;
    }
    try {
      const data = await idAuth.continue(redirect);
      navigateOrLeave(data.redirect, true);
    } catch (nextError) {
      setRememberedAccount(null);
      setShowSignInOptions(true);
      setError(messageFrom(nextError));
      setBusy(false);
    }
  };

  const useAnotherAccount = async () => {
    setBusy(true);
    setError(null);
    try { await idAuth.logout('local'); } catch { /* A new sign-in can replace an expired session. */ }
    setRememberedAccount(null);
    setShowSignInOptions(true);
    setBusy(false);
  };

  if (['/auth', '/signin', '/sign-in', '/log-in'].includes(location.pathname)) return <Navigate to={`/login${location.search}`} replace />;

  if (mode === 'error') return (
    <Shell eyebrow="Secure link" title="This link no longer works" subtitle="The authentication request is invalid, expired, or has already been used.">
      <div className="space-y-4">
        <Notice error>Start a new sign-in or recovery request. No account data was changed.</Notice>
        <Link to={authModeHref('/login')} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#17171a] px-5 py-3.5 text-sm font-bold text-white dark:bg-white dark:text-black">Back to sign in <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </Shell>
  );

  if (mode === 'forgot') return (
    <Shell eyebrow="Account recovery" title="Forgot your password?" subtitle="Enter the email connected to your Wersee ID. We will send a secure verification code if the account exists.">
      <form onSubmit={submitRecovery} className="space-y-5">
        <label className="block space-y-2 text-xs font-semibold text-black/62 dark:text-white/58">
          Email address
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/28 dark:text-white/28" />
            <input type="email" autoComplete="email" required autoFocus className={`${fieldClass} pl-11`} value={email} onChange={(event) => setEmail(event.target.value)} />
          </div>
        </label>
        {error && <Notice error>{error}</Notice>}
        <SubmitButton busy={busy}>Send verification code</SubmitButton>
        <Link to={authModeHref('/login')} className="flex items-center justify-center gap-2 text-xs font-semibold text-black/48 hover:text-black dark:text-white/45 dark:hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Back to sign in</Link>
      </form>
    </Shell>
  );

  if (mode === 'verify') {
    const deliveryCopy: Record<IdVerificationDeliveryStatus, string> = {
      requested: 'Requesting securely', sent: 'Sent through Resend', delivered: 'Delivered to your inbox', verified: 'Verified', delivery_delayed: 'Delivery is taking longer', failed: 'Delivery failed', bounced: 'Email bounced', suppressed: 'Delivery suppressed', expired: 'Code expired',
    };
    return (
      <Shell eyebrow="Email verification" title="Enter your code" subtitle={<>We sent an 8-digit code to <strong className="font-semibold text-black/75 dark:text-white/75">{email || 'your email address'}</strong>.</>}>
        <form onSubmit={submitVerification} className="space-y-5">
          <div
            onClick={() => codeInput.current?.focus()}
            className="relative grid w-full grid-cols-8 gap-1.5 sm:gap-2"
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <span key={index} className={`grid aspect-[0.82] place-items-center rounded-[14px] border text-lg font-black transition sm:text-xl ${verificationCode[index] ? 'border-[#7358ef]/40 bg-[#7358ef]/8 text-[#593bdd] dark:border-[#a894ff]/40 dark:bg-[#a894ff]/10 dark:text-[#c8bcff]' : 'border-black/10 bg-black/[0.035] text-black/22 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/22'}`}>
                {verificationCode[index] || '·'}
              </span>
            ))}
            <input
              ref={codeInput}
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 8))}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{8}"
              maxLength={8}
              required
              autoFocus
              className="absolute inset-0 cursor-text opacity-0"
              aria-label="Verification code"
            />
          </div>

          <div className="flex items-center justify-between rounded-2xl bg-black/[0.035] px-3.5 py-3 text-[11px] text-black/46 dark:bg-white/[0.05] dark:text-white/42">
            <span className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${['failed', 'bounced', 'suppressed'].includes(deliveryStatus) ? 'bg-red-500' : deliveryStatus === 'delivered' ? 'bg-emerald-500' : 'animate-pulse bg-[#7358ef]'}`} /> {deliveryCopy[deliveryStatus]}</span>
            <span>Expires in 10 min</span>
          </div>

          {error && <Notice error>{error}</Notice>}
          {notice && <Notice>{notice}</Notice>}
          <SubmitButton busy={busy} disabled={verificationCode.length !== 8}>Verify and continue</SubmitButton>
          <button type="button" onClick={() => void resendVerification()} disabled={busy || resendSeconds > 0} className="block w-full text-center text-xs font-semibold text-[#6b50e8] disabled:text-black/30 dark:text-[#ae9dff] dark:disabled:text-white/25">
            {resendSeconds > 0 ? `Request a new code in ${resendSeconds}s` : 'Send a new code'}
          </button>
        </form>
      </Shell>
    );
  }

  if (mode === 'reset') return (
    <Shell eyebrow="Account recovery" title="Create a new password" subtitle="Choose a strong password you have not used for this Wersee ID before.">
      <form onSubmit={submitReset} className="space-y-4">
        <PasswordField label="New password" autoComplete="new-password" value={password} onChange={setPassword} />
        <PasswordField label="Confirm new password" autoComplete="new-password" value={confirm} onChange={setConfirm} />
        {error && <Notice error>{error}</Notice>}
        <SubmitButton busy={busy}>Save new password</SubmitButton>
      </form>
    </Shell>
  );

  if (mode === 'mfa') return (
    <Shell eyebrow="Two-factor security" title="Verify it’s you" subtitle="Enter the current code from your authenticator app to finish signing in.">
      <form onSubmit={submitMfa} className="space-y-5">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-[22px] bg-[#7258ef]/10 text-[#5b3de0] dark:bg-[#a894ff]/10 dark:text-[#b9aaff]"><ShieldCheck className="h-8 w-8" /></div>
        <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6,10}" required autoFocus placeholder="Authentication code" className={`${fieldClass} text-center text-lg tracking-[0.35em]`} value={mfaCode} onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 10))} />
        {error && <Notice error>{error}</Notice>}
        <SubmitButton busy={busy} disabled={!mfa}>Verify and continue</SubmitButton>
      </form>
    </Shell>
  );

  const isSignup = mode === 'signup';
  return (
    <Shell
      eyebrow={rememberedAccount && !showSignInOptions ? 'Remembered account' : isSignup ? 'Get started' : 'Welcome back'}
      title={isSignup ? 'Create your Wersee ID' : rememberedAccount && !showSignInOptions ? 'Good to see you again' : 'Sign in to Wersee'}
      subtitle={isSignup ? 'Create one secure account for every Wersee service.' : rememberedAccount && !showSignInOptions ? 'Continue with the account securely remembered on this device.' : 'Access your work, communities and payments with one identity.'}
    >
      <div className="space-y-5">
        {mode === 'login' && checkingRemembered && (
          <div role="status" className="flex items-center justify-center gap-2 rounded-2xl bg-black/[0.035] px-4 py-3 text-xs text-black/40 dark:bg-white/[0.05] dark:text-white/40"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking this device</div>
        )}

        {mode === 'login' && rememberedAccount && !showSignInOptions && (
          <div className="space-y-5">
            <div className="rounded-[26px] border border-black/8 bg-black/[0.025] p-5 text-center dark:border-white/10 dark:bg-white/[0.045]">
              <div className="mx-auto grid h-18 w-18 place-items-center overflow-hidden rounded-[22px] bg-gradient-to-br from-[#7358ef]/25 to-[#4cc9ff]/20 text-xl font-black">
                {rememberedAccount.identity.avatar_url ? <img src={rememberedAccount.identity.avatar_url} alt="" className="h-full w-full object-cover" /> : rememberedName.slice(0, 1).toUpperCase()}
              </div>
              <h2 className="mt-4 text-lg font-bold">{rememberedName}</h2>
              <p className="mt-1 truncate text-xs text-black/40 dark:text-white/40">{rememberedAccount.user.email}</p>
              <button type="button" disabled={busy} onClick={() => void continueRememberedAccount()} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#17171a] px-5 py-3.5 text-sm font-bold text-white disabled:opacity-50 dark:bg-white dark:text-black">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Continue as {rememberedName}
              </button>
              <button type="button" disabled={busy} onClick={() => void useAnotherAccount()} className="mt-3 text-xs font-semibold text-black/45 hover:text-black disabled:opacity-50 dark:text-white/45 dark:hover:text-white">Use another account</button>
            </div>
            <p className="text-center text-[11px] leading-5 text-black/35 dark:text-white/32">This device stays remembered for up to 30 days unless you sign out or change a security setting.</p>
          </div>
        )}

        {shouldShowSignInOptions && (
          <>
            <div className="grid grid-cols-2 rounded-full bg-black/[0.055] p-1 text-xs font-semibold dark:bg-white/[0.07]">
              <Link to={authModeHref('/login')} className={`rounded-full px-4 py-2.5 text-center transition ${!isSignup ? 'bg-white text-black shadow-sm dark:bg-white dark:text-black' : 'text-black/42 dark:text-white/42'}`}>Sign in</Link>
              <Link to={authModeHref('/signup')} className={`rounded-full px-4 py-2.5 text-center transition ${isSignup ? 'bg-white text-black shadow-sm dark:bg-white dark:text-black' : 'text-black/42 dark:text-white/42'}`}>Create account</Link>
            </div>

            <form onSubmit={submitCredentials} className="space-y-4">
              {isSignup && (
                <label className="block space-y-2 text-xs font-semibold text-black/62 dark:text-white/58">
                  Display name
                  <div className="relative"><UserPlus className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/28 dark:text-white/28" /><input autoComplete="name" maxLength={160} required className={`${fieldClass} pl-11`} value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></div>
                </label>
              )}
              <label className="block space-y-2 text-xs font-semibold text-black/62 dark:text-white/58">
                Email address
                <div className="relative"><Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/28 dark:text-white/28" /><input type="email" autoComplete="email" required className={`${fieldClass} pl-11`} value={email} onChange={(event) => setEmail(event.target.value)} /></div>
              </label>
              <PasswordField label="Password" autoComplete={isSignup ? 'new-password' : 'current-password'} value={password} onChange={setPassword} forgotHref={!isSignup ? authModeHref('/forgot-password') : undefined} />
              {isSignup && <PasswordField label="Confirm password" autoComplete="new-password" value={confirm} onChange={setConfirm} />}
              {error && <Notice error>{error}</Notice>}
              {notice && <Notice>{notice}</Notice>}
              <SubmitButton busy={busy}>{isSignup ? 'Create Wersee ID' : 'Sign in securely'}</SubmitButton>
            </form>

            {providers.some((item) => item.enabled && ['google', 'azure', 'github'].includes(item.id)) && (
              <>
                <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-[0.16em] text-black/25 dark:text-white/25"><span className="h-px flex-1 bg-black/8 dark:bg-white/10" /> or continue with <span className="h-px flex-1 bg-black/8 dark:bg-white/10" /></div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {googleProvider && <button type="button" aria-label="Continue with Google" disabled={busy} onClick={() => void startOAuth('google')} className="flex items-center justify-center gap-2 rounded-full border border-black/8 bg-white px-3 py-3 text-xs font-semibold hover:bg-black/[0.025] disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.055] dark:hover:bg-white/[0.09]"><span className="grid h-4 w-4 place-items-center rounded-full bg-white text-[10px] font-black text-[#4285f4]">G</span>Google</button>}
                  {providers.some((item) => item.id === 'azure' && item.enabled) && <button type="button" disabled={busy} onClick={() => void startOAuth('azure')} className="flex items-center justify-center gap-2 rounded-full border border-black/8 bg-white px-3 py-3 text-xs font-semibold hover:bg-black/[0.025] disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.055] dark:hover:bg-white/[0.09]"><span className="grid grid-cols-2 gap-0.5"><i className="h-1.5 w-1.5 bg-[#f25022]" /><i className="h-1.5 w-1.5 bg-[#7fba00]" /><i className="h-1.5 w-1.5 bg-[#00a4ef]" /><i className="h-1.5 w-1.5 bg-[#ffb900]" /></span>Microsoft</button>}
                  {providers.some((item) => item.id === 'github' && item.enabled) && <button type="button" disabled={busy} onClick={() => void startOAuth('github')} className="flex items-center justify-center gap-2 rounded-full border border-black/8 bg-white px-3 py-3 text-xs font-semibold hover:bg-black/[0.025] disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.055] dark:hover:bg-white/[0.09]"><Github className="h-4 w-4" />GitHub</button>}
                </div>
              </>
            )}

            <div className="flex items-start gap-2.5 rounded-2xl bg-black/[0.035] p-3 text-[11px] leading-5 text-black/42 dark:bg-white/[0.045] dark:text-white/38"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /><span>Your credentials stay inside the secure Wersee ID gateway. Provider and handoff protections remain enabled.</span></div>
          </>
        )}
      </div>
    </Shell>
  );
};
